// PG-24 — the e1RM axis: which exercise SPEAKS for a movement pattern.
//
// Split out of `tierOneMath.ts` on 2026-09-17. That file was at 476/500 and is
// imported by four panels; this concept is imported by exactly one
// (`E1RMTrendsPanel`) plus the package barrel. Keeping the occupant/predecessor
// work here means the pattern-balance, pain-volume and reps-at-load panels do
// not re-inflate a file they share, and the e1RM rules have ONE home.
import {
  DAY_MS,
  detectRegression,
  e1rmPointsByTrack,
  meetsTrendThreshold,
  type E1rmPoint,
  type Exercise,
  type MovementPattern,
  type RegressionResult,
  type TimedSessionResult,
} from "@heraion/shared";
import { deriveOccupancy } from "./e1rmOccupancy";

// measurement-contract §3 — how many of a series' points are a floor worth
// trusting (`proven`/`probable`). ⛔ Explicit predicate, never an ordering of
// the confidence verdicts (the union is not a scale). ONE place so the
// champion track and each alternate machine grade their OWN points the same
// way — EQ-1-HIST-TRUST: an alternate is untrusted on the strength of its own
// series, never by inheriting the champion's.
const trustedPointCount = (points: readonly E1rmPoint[]): number =>
  points.filter(
    (pt) => pt.confidence === "proven" || pt.confidence === "probable",
  ).length;

// =========================================================================
// E1RMTrendsPanel — top lift per pattern + sparkline + regression flag
// =========================================================================
// Isolation / cardio / other are excluded because a regression alert on a
// bicep curl or a treadmill bout isn't actionable like one on the squat.
// The order here is the rendering order.
export const TREND_PATTERNS: MovementPattern[] = [
  "squat",
  "hinge",
  "horizontal-push",
  "vertical-push",
  "horizontal-pull",
  "vertical-pull",
  "lunge",
  "carry",
  "core",
];


/**
 * One of the champion exercise's OTHER machines (EQ-1-HIST slice 2). Carried
 * so the row can put them behind a tap instead of on screen: Antenore's call
 * was one row per pattern, principal machine shown, the rest reachable.
 *
 * ⛔ Deliberately thinner than the champion — a name and a number, no series
 * and no trend. A second sparkline behind a disclosure would be a second
 * reading competing with the one the row is making; if it turns out he wants
 * the shape too, that is a slice of its own, not a field added on spec.
 */
// Internal to this module — the champion row consumes it inline, nothing
// outside imports it, so it stays unexported (N1 deadcode sweep).
type E1RMTrendAlternate = {
  equipmentInstance: string;
  latest: number;
  pointCount: number;
  /**
   * §3 trust on THIS machine's own series — `proven`/`probable` points only.
   * When 0, the alternate's numbers are a floor we can't stand behind, exactly
   * like the champion's `trustedCount === 0` but scoped to this track. The row
   * grades it with the same "?" so an opened machine list never shows an
   * untrusted e1RM as if it were measured (EQ-1-HIST-TRUST).
   */
  trustedCount: number;
};

/**
 * The exercise that held this pattern BEFORE the current occupant took it —
 * PG-24, and his words exactly: *"il precedente significa il precedente
 * occupante della casella"*. ⛔ Not the runner-up on weight, and not simply
 * the next-most-recent lift: the one that was actually winning the cell until
 * the handover.
 *
 * ⚠️ DERIVED, not stored (slice A, 2026-09-17). Persisting it is decided but
 * is the next slice; until then the handover is re-read from the window on
 * every render. The two must agree, which is why the derivation walks the
 * election forward in time instead of guessing from today's standings —
 * whatever gets written later has to equal what this returns.
 */
export type E1RMTrendPredecessor = {
  slug: string;
  snapshot: string | undefined;
  equipmentInstance?: string;
  /** Its own series, so the row can draw it as the second curve. */
  sorted: E1rmPoint[];
  latest: number;
};

export type E1RMTrendEntry = {
  pattern: MovementPattern;
  slug: string;
  snapshot: string | undefined;
  /**
   * Which machine produced the numbers on this row, when the athlete named
   * one. Absent = the Default machine, which is every lift logged before
   * EQ-1-HIST and all of production today.
   */
  equipmentInstance?: string;
  /**
   * The same exercise on the athlete's other machines, best first. Empty for
   * every untagged exercise, so the row renders exactly as it always did.
   */
  alternates: E1RMTrendAlternate[];
  /**
   * Present only when the window actually shows a handover. Absent means the
   * current occupant has held the cell for the whole window — which is the
   * normal case for someone who trains one exercise per pattern, and the row
   * must then look exactly as it always did.
   */
  previous?: E1RMTrendPredecessor;
  sorted: E1rmPoint[];
  latest: number;
  reg: RegressionResult;
  /**
   * How many of this champion's points are a floor worth trusting —
   * `proven` or `probable` (measurement-contract §3). When 0, points exist but
   * none went near the limit: the trend arrow would be a confident line drawn
   * through sets we know nothing about, so the panel shows the honest
   * "not measurable yet" state instead. ⛔ Counted by explicit predicate, never
   * by ordering the confidence verdicts (E1RM_CONFIDENCES is not a scale).
   */
  trustedCount: number;
};

/**
 * What the e1RM panel draws: the patterns that HAVE an occupant, and the
 * patterns that have one to talk about but no longer have one to show.
 */
export type E1RMTrendRanking = {
  /** Occupied cells, in TREND_PATTERNS order. */
  rows: E1RMTrendEntry[];
  /**
   * PG-24 — patterns the athlete trained in the window where nothing cleared
   * the admissibility threshold. The panel states the measure on these instead
   * of leaving a hole, because a pattern that was there last week and is gone
   * this week would otherwise vanish with no reason given.
   *
   * ⛔ NOT the same as a pattern with nothing logged. Those are absent from
   * both lists and stay undrawn, exactly as before: turning every untrained
   * pattern into a sentence would hand a new athlete a wall of nine
   * apologies, and Progress is being slimmed (measurement-contract §1).
   */
  unmetPatterns: MovementPattern[];
  /**
   * PG-24 — every exercise the athlete can ASK to see compared, keyed by slug.
   *
   * His one constraint on the manual choice (2026-09-16): *"la scelta
   * dell'utente può farla, solo sugli esercizi che hanno e1RM registrati"* —
   * 42 of his 76. ⛔ Deliberately NOT filtered by `meetsTrendThreshold`: that
   * bar decides who may OCCUPY a pattern unasked, and a comparison the athlete
   * asked for by name is not unasked. Filtering here would hide the very
   * exercise someone opened the picker to look for, with no way to tell why.
   */
  comparable: Map<string, E1RMTrendPredecessor>;
};

// PG-1 period selector: `windowDays` bounds both the sparkline aggregation and
// the regression fit (windowWeeks = windowDays/7). Default 84d (= 12 weeks)
// keeps the pre-selector behaviour identical.
export function rankE1RMTrends(
  results: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  windowDays = 84,
): E1RMTrendRanking {
  const cutoffWindowMs = nowMs - windowDays * DAY_MS;
  const slugToPattern = new Map<string, MovementPattern>();
  for (const ex of catalog) {
    slugToPattern.set(ex.slug, ex.movementPattern ?? "other");
  }

  // Aggregate e1RM points per exercise within the 12-week window. Name is
  // resolved at render time via the live catalog (FK = exerciseSlug); the
  // snapshot is kept only as a deleted-row fallback.
  type Track = {
    /** The exercise — resolved from the series, never from the map key. */
    slug: string;
    /** The machine, when named. Absent = the Default. */
    instance: string | undefined;
    pattern: MovementPattern;
    snapshot: string | undefined;
    points: E1rmPoint[];
    latest: number;
  };
  // slug → every machine that exercise was lifted on in the window.
  const byExercise = new Map<string, Track[]>();
  // The series come from the shared funnel (e1rmPoints.ts). The pattern is the
  // only thing this panel adds, and it is a catalog lookup — not part of what
  // makes an observation an observation.
  //
  // ⛔ The map key is a TRACK key (exercise + machine, EQ-1-HIST), NOT a slug:
  // for a tagged lift it is an opaque composite that no catalog contains. The
  // exercise rides on `series.slug`, and that is what the catalog lookup and
  // the entry below must both use — keying either off the map key makes the
  // pattern resolve to "other", which TREND_PATTERNS drops, so a lift on a
  // named machine would silently stop being able to win its pattern. Silently:
  // no error, no log, and no test would see it while nobody tags.
  for (const series of e1rmPointsByTrack(results, {
    fromMs: cutoffWindowMs,
    toMs: nowMs,
  }).values()) {
    const bucket = byExercise.get(series.slug);
    const track: Track = {
      slug: series.slug,
      instance: series.equipmentInstance,
      pattern: slugToPattern.get(series.slug) ?? "other",
      snapshot: series.snapshot,
      points: series.points,
      // Already chronological — the funnel sorts once so no consumer has to.
      latest: series.points[series.points.length - 1]?.kgPerRep1 ?? 0,
    };
    if (bucket) bucket.push(track);
    else byExercise.set(series.slug, [track]);
  }

  type Champ = {
    slug: string;
    snapshot: string | undefined;
    instance: string | undefined;
    alternates: E1RMTrendAlternate[];
    previous?: E1RMTrendPredecessor;
    sorted: E1rmPoint[];
    latest: number;
  };

  // ⭐ Which machine SPEAKS for the exercise (EQ-1-HIST slice 2). The one that
  // produced the exercise's top reading, because that is the number the panel
  // promises in its own help text — "the exercise where your top e1RM is
  // highest". Picking the busiest machine instead would put a number on the
  // row that is NOT the one that won the pattern, and the row would quietly
  // contradict the legend above it.
  //
  // ⇒ Volume of evidence is the TIE-BREAK, not the rule: same top reading, the
  // machine you have actually used more wins, and failing that the more recent
  // one. Deterministic in every case, so the row cannot flicker between two
  // machines across renders.
  //
  // 📐 Mine, not his (2026-08-19). His words were "the machine with more data
  // (or the most recent)"; the coherence problem above only shows up in code,
  // so this reading is my call and he should overrule it if he meant it
  // literally.
  const speaksForExercise = (tracks: Track[]): Track =>
    tracks.reduce((best, t) => {
      if (t.latest !== best.latest) return t.latest > best.latest ? t : best;
      if (t.points.length !== best.points.length) {
        return t.points.length > best.points.length ? t : best;
      }
      const tAt = t.points[t.points.length - 1]?.atMs ?? 0;
      const bAt = best.points[best.points.length - 1]?.atMs ?? 0;
      return tAt > bAt ? t : best;
    });

  // ⭐ PG-24 — the pattern's CANDIDATES: one principal machine per exercise.
  // Who occupies the pattern is no longer decided here, because freshness is a
  // property of an INSTANT: the cell has a history, and the row has to be able
  // to name who held it before the current occupant took over.
  type Candidate = {
    slug: string;
    snapshot: string | undefined;
    instance: string | undefined;
    points: E1rmPoint[];
    latest: number;
    alternates: E1RMTrendAlternate[];
  };
  const candidatesByPattern = new Map<MovementPattern, Candidate[]>();
  // Patterns that were trained in the window but whose every series was too
  // short or too bunched to speak. Kept apart from "never trained" — see
  // E1RMTrendRanking.unmetPatterns.
  const trainedButUnmet = new Set<MovementPattern>();
  for (const tracks of byExercise.values()) {
    // Every track of one exercise shares the exercise's pattern (it is a
    // catalog lookup on the slug), so the machine election cannot change it.
    const pattern = tracks[0]?.pattern ?? "other";
    if (!TREND_PATTERNS.includes(pattern)) continue;
    // ⭐ PG-24 — admissibility is decided per TRACK, and BEFORE the machine
    // election. Electing first and filtering after looks equivalent and is
    // not: one heavy session on a machine you just tried would win
    // `speaksForExercise` (top reading), fail the threshold on its own
    // two-point series, and take the exercise's REAL trend down with it —
    // the Default track, with months of points, would never get to compete.
    const eligible = tracks.filter((t) => meetsTrendThreshold(t.points));
    if (eligible.length === 0) {
      trainedButUnmet.add(pattern);
      continue;
    }
    const principal = speaksForExercise(eligible);
    const bucket = candidatesByPattern.get(pattern);
    const candidate: Candidate = {
      slug: principal.slug,
      snapshot: principal.snapshot,
      instance: principal.instance,
      points: principal.points,
      latest: principal.latest,
      // Everything else the same exercise was lifted on, heaviest first. The
      // Default machine has no name to show, so it cannot appear here — it is
      // either the principal or it is not in the picture.
      //
      // ⛔ Drawn from ALL tracks, not the eligible ones: PG-24 governs who
      // OCCUPIES a pattern, and an alternate occupies nothing. It is a name
      // and a number behind a tap, with no series and no trend, so a
      // threshold about trends has nothing to say about it — filtering here
      // would silently delete machines the athlete really did use.
      alternates: tracks
        .filter((t) => t !== principal && t.instance !== undefined)
        .sort((a, b) => b.latest - a.latest)
        .map((t) => ({
          equipmentInstance: t.instance as string,
          latest: t.latest,
          pointCount: t.points.length,
          trustedCount: trustedPointCount(t.points),
        })),
    };
    if (bucket) bucket.push(candidate);
    else candidatesByPattern.set(pattern, [candidate]);
  }

  // Built from ALL tracks, before any threshold: see `comparable` above.
  const comparable = new Map<string, E1RMTrendPredecessor>();
  for (const tracks of byExercise.values()) {
    const speaker = speaksForExercise(tracks);
    comparable.set(speaker.slug, {
      slug: speaker.slug,
      snapshot: speaker.snapshot,
      ...(speaker.instance ? { equipmentInstance: speaker.instance } : {}),
      sorted: speaker.points,
      latest: speaker.latest,
    });
  }

  const byPattern = new Map<MovementPattern, Champ>();
  for (const [pattern, candidates] of candidatesByPattern) {
    const held = deriveOccupancy(candidates, nowMs);
    // Every candidate cleared admissibility, but all of them may have gone
    // stale — a pattern trained hard and then dropped. That is an unmet cell,
    // the same sentence the athlete already gets, not a hole.
    if (held === undefined) {
      trainedButUnmet.add(pattern);
      continue;
    }
    const { current, previous } = held;
    byPattern.set(pattern, {
      slug: current.slug,
      snapshot: current.snapshot,
      instance: current.instance,
      alternates: current.alternates,
      ...(previous
        ? {
            previous: {
              slug: previous.slug,
              snapshot: previous.snapshot,
              ...(previous.instance
                ? { equipmentInstance: previous.instance }
                : {}),
              sorted: previous.points,
              latest: previous.latest,
            },
          }
        : {}),
      sorted: current.points,
      latest: current.latest,
    });
  }

  const rows = TREND_PATTERNS.flatMap((p) => {
    const champ = byPattern.get(p);
    if (!champ) return [];
    const reg = detectRegression(champ.sorted, nowMs, windowDays / 7);
    // A floor worth trusting = the point went near/to the limit. Same §3
    // predicate every alternate machine grades its own series with.
    const trustedCount = trustedPointCount(champ.sorted);
    return [
      {
        pattern: p,
        slug: champ.slug,
        snapshot: champ.snapshot,
        ...(champ.instance ? { equipmentInstance: champ.instance } : {}),
        alternates: champ.alternates,
        ...(champ.previous ? { previous: champ.previous } : {}),
        sorted: champ.sorted,
        latest: champ.latest,
        reg,
        trustedCount,
      },
    ];
  });

  return {
    rows,
    comparable,
    // A pattern only counts as unmet if nothing ELSE won it: one exercise can
    // fail the threshold while a sibling in the same pattern clears it, and
    // that cell is occupied, not empty. Rendering order stays TREND_PATTERNS'.
    unmetPatterns: TREND_PATTERNS.filter(
      (p) => trainedButUnmet.has(p) && !byPattern.has(p),
    ),
  };
}
